import {useMemo} from 'react';
import articlesData from '../articles.json';
import {Article, ArticlesByCategory} from '../types';
import {useHelpContentLocale} from './useHelpLocale';

const allArticles = articlesData as Article[];

export function useArticles() {
  // The URL decides the locale (bare /help/* is English, /help/fr/* is French)
  // so a canonical help URL serves the same content to every visitor — the UI
  // language must never leak other-locale content onto the English URLs.
  const locale = useHelpContentLocale();

  // Filter articles by the URL locale, fallback to English
  const articles = useMemo<Article[]>(() => {
    const localeArticles = allArticles.filter((a) => a.locale === locale);
    if (localeArticles.length > 0) {
      return localeArticles;
    }
    // Fallback to English
    return allArticles.filter((a) => a.locale === 'en');
  }, [locale]);

  const articlesByCategory = useMemo<ArticlesByCategory>(() => {
    const grouped: ArticlesByCategory = {};
    for (const article of articles) {
      if (!grouped[article.category]) {
        grouped[article.category] = [];
      }
      grouped[article.category].push(article);
    }
    return grouped;
  }, [articles]);

  const getArticleBySlug = (slug: string): Article | undefined => {
    // First try to find in current locale
    const localeArticle = articles.find((a) => a.slug === slug);
    if (localeArticle) {
      return localeArticle;
    }
    // Fallback to English if not found in current locale
    return allArticles.find((a) => a.slug === slug && a.locale === 'en');
  };

  const getRelatedArticles = (article: Article): Article[] => {
    return article.relatedSlugs
      .map((slug) => getArticleBySlug(slug))
      .filter((a): a is Article => a !== undefined);
  };

  return {
    articles,
    articlesByCategory,
    getArticleBySlug,
    getRelatedArticles,
  };
}
